<template>
  <transition :name="transitionName" :appear="transitionAppear">
    <div :class="shouldRender" :style="shouldRender" v-if="shouldRender" v-on="$listeners">
      <slot />
    </div>
  </transition>
</template>

<script>
  import Vue from 'vue'

  const elementType = window.HTMLElement

  const validator = (prop, context, expression, message) => {
    if (expression) {
      return true
    }

    Vue.util.warn(`The ${prop} prop is invalid. ${message}.`, context)

    return false
  }

  export default {
    name: 'RenderPortal',
    props: {
      portalIf: {
        type: null,
        validator: (value) => validator('portal-if', this, value === null || typeof value === 'boolean', 'You should pass a Boolean value')
      },
      transitionName: String,
      transitionAppear: Boolean,
      portalTarget: {
        type: elementType,
        validator: (value) => validator('portal-target', this, value && value instanceof elementType, 'You should pass a valid HTMLElement')
      }
    },
    data: () => ({
      leaveTimeout: null,
      targetEl: null
    }),
    computed: {
      shouldRender () {
        return typeof this.portalIf === 'boolean' ? this.portalIf : true
      },
      leaveClass () {
        return this.transitionName + '-leave'
      },
      leaveActiveClass () {
        return this.transitionName + '-leave-active'
      },
      leaveToClass () {
        return this.transitionName + '-leave-to'
      }
    },
    watch: {
      portalTarget (newTarget, oldTarget) {
        this.changeParentEl()
        newTarget.appendChild(this.$el)

        if (oldTarget) {
          this.$forceUpdate()
        }
      }
    },
    methods: {
      getTransitionDuration () {
        const duration = window.getComputedStyle(this.$el).transitionDuration
        const num = parseFloat(duration, 10)
        let unit = duration.match(/m?s/)
        let milliseconds = null

        if (unit) {
          unit = unit[0]
        }

        switch (unit) {
        case 's':
          milliseconds = num * 1000
          break

        case 'ms':
          milliseconds = num
          break

        default:
          milliseconds = 0
          break
        }

        return milliseconds
      },
      changeParentEl () {
        this.targetEl = this.portalTarget || document.body
        this.$options._parentElm = this.targetEl
      },
      killGhostElement () {
        if (this.targetEl) {
          const children = Array.from(this.targetEl.childNodes)

          if (children.includes(this.$el)) {
            this.targetEl.removeChild(this.$el)
          }
        }
      },
      destroyElement () {
        window.requestAnimationFrame(() => {
          this.$el.classList.remove(this.leaveClass)
          this.$el.classList.remove(this.leaveActiveClass)
          this.$el.classList.remove(this.leaveToClass)
          this.killGhostElement()
        })
      }
    },
    created () {
      this.changeParentEl()
    },
    async beforeDestroy () {
      if (this.$el.classList) {
        this.$el.classList.add(this.leaveClass)
        this.$el.classList.add(this.leaveActiveClass)
        await this.$nextTick()
        this.$el.classList.add(this.leaveToClass)

        window.clearTimeout(this.leaveTimeout)
        this.leaveTimeout = window.setTimeout(this.destroyElement, this.getTransitionDuration())
      } else {
        this.killGhostElement()
      }
    }
  }
</script>
